import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { brandInfo, faqItems } from '@/mocks/articles';

type Tab = 'intro' | 'timeline' | 'faq' | 'contact';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<Tab>('intro');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'intro', label: '品牌介绍', icon: 'ri-building-line' },
    { key: 'timeline', label: '发展历程', icon: 'ri-road-map-line' },
    { key: 'faq', label: '常见问题', icon: 'ri-question-answer-line' },
    { key: 'contact', label: '联系我们', icon: 'ri-mail-send-line' },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${brandInfo.name} - 品牌介绍与联系方式`,
    description: brandInfo.description,
    url: 'https://mitaomedia.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: brandInfo.name,
      description: brandInfo.description,
      foundingDate: brandInfo.founded,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: brandInfo.contact.email,
        telephone: brandInfo.contact.phone,
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormError('');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9slnkl8o1vn02ekuhhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      const code = parsed?.code as string | undefined;
      const metaMsg = (parsed?.meta as Record<string, unknown>)?.message as string | undefined;
      const serverMsg = (metaMsg || (parsed?.message as string) || text || '').toString();

      if (res.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式"
        description={brandInfo.description}
        canonicalUrl="https://mitaomedia.com/about"
        schemaJson={(activeTab === 'faq' || activeTab === 'intro') ? [aboutSchema, ...(activeTab === 'faq' ? [faqSchema] : [])] : aboutSchema}
      />

      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb />

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">关于蜜桃传媒</h1>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">{brandInfo.slogan}</p>
          </div>

          <div className="bg-background-100 rounded-lg px-1 py-1 mb-8 inline-flex flex-wrap gap-1" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => { setActiveTab(tab.key); setFormStatus('idle'); setFormError(''); }}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                }`}
              >
                <i className={`${tab.icon} mr-1.5`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'intro' && (
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">品牌故事</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{brandInfo.description}</p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    自{brandInfo.founded}年成立以来，蜜桃传媒始终坚持"内容为王、品质至上"的核心理念。我们深知，在信息爆炸的时代，真正有价值的内容才能穿越噪音、直抵人心。
                  </p>
                  <div className="bg-accent-100/70 rounded-lg p-5 border border-accent-200/50 mt-6">
                    <h3 className="font-heading text-sm font-bold text-accent-700 mb-2">我们的使命</h3>
                    <p className="text-sm text-accent-800">{brandInfo.mission}</p>
                    <h3 className="font-heading text-sm font-bold text-accent-700 mt-4 mb-2">我们的愿景</h3>
                    <p className="text-sm text-accent-800">{brandInfo.vision}</p>
                  </div>
                </div>
                <div>
                  <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20elegant%20media%20company%20headquarters%20building%20with%20warm%20coral%20and%20neutral%20interior%20design%2C%20sophisticated%20creative%20workspace%20with%20natural%20light%2C%20professional%20corporate%20atmosphere%20with%20art%20decorations%2C%20editorial%20photography%20style%20with%20warm%20tones&width=800&height=600&seq=about-brand&orientation=landscape"
                      alt="蜜桃传媒品牌形象"
                      title="蜜桃传媒 - 专注优质内容创作与传播"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {brandInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">{stat.value}</div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="max-w-3xl mx-auto">
              <div className="relative pl-8 border-l-2 border-accent-200">
                {brandInfo.timeline.map((item, i) => (
                  <div key={i} className="mb-8 relative">
                    <div className="absolute -left-[calc(1rem+5px)] top-0 w-4 h-4 rounded-full bg-accent-500 border-2 border-background-50" />
                    <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                      <span className="inline-block px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-2">
                        {item.year}
                      </span>
                      <p className="text-sm text-foreground-700">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'faq' && (
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-col gap-4">
                {faqItems.map((item, i) => (
                  <details key={i} className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden">
                    <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-semibold text-foreground-900 list-none">
                      <span>{item.question}</span>
                      <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">联系方式</h2>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                      <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100">
                        <i className="ri-map-pin-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">公司地址</p>
                        <p className="text-sm text-foreground-500 mt-0.5">{brandInfo.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                      <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                        <p className="text-sm text-foreground-500 mt-0.5">{brandInfo.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                      <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100">
                        <i className="ri-phone-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">客服热线</p>
                        <p className="text-sm text-foreground-500 mt-0.5">{brandInfo.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                      <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100">
                        <i className="ri-wechat-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">官方微信</p>
                        <p className="text-sm text-foreground-500 mt-0.5">{brandInfo.contact.wechat}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">发送消息</h2>
                  <form
                    className="flex flex-col gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <div className="form-honeypot">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">您的姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">电子邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您想说的话（500字以内）"
                      />
                    </div>
                    <button
                      type="submit"
                      className="whitespace-nowrap w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                    >
                      发送消息
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-3 rounded-md bg-accent-100 text-accent-700 text-sm text-center">
                        消息已发送成功，我们会尽快与您联系！
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 rounded-md bg-primary-100 text-primary-700 text-sm text-center">
                        {formError || '提交失败，请稍后重试'}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </>
  );
}